import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { Send, MessageCircle, Loader2, FileText, Mic, MicOff, Sparkles, MessageSquareQuote, Search, X, MapPin, Phone, Clock, Copy, Play, Pause, Trash2, Save, Volume2, Plus, Edit2, Gift, Paperclip, Forward, Image as ImageIcon, Video, File } from "lucide-react";
import ShareBuilder from "../pages/admin/ShareBuilder";

import { MetaTemplateSend } from "./MetaTemplateSend";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Normalize any axios error response into a plain user-facing string.
 * FastAPI's `detail` can be a **string** or a **Pydantic validation array**
 * on 422s. Rendering an object inside JSX crashes the whole app tree, so
 * this helper collapses every shape to a friendly string.
 */
function humanizeError(err, fallback = "Что-то пошло не так") {
  const d = err?.response?.data?.detail;
  if (typeof d === "string" && d.trim()) return d;
  if (Array.isArray(d) && d.length) {
    const first = d[0];
    if (typeof first === "string") return first;
    if (first && typeof first.msg === "string") return first.msg;
  }
  if (typeof err?.message === "string" && err.message) return err.message;
  return fallback;
}

/**
 * Substitute {client_name}, {product}, {price}, {city}, {manager_name}
 * placeholders inside a template. Missing values become an empty string
 * so the resulting text still reads naturally.
 */
function fillTemplate(text, ctx) {
  return (text || "").replace(/\{(\w+)\}/g, (_, key) => {
    const v = ctx?.[key];
    return (v === undefined || v === null) ? "" : String(v);
  });
}

/**
 * WhatsApp chat panel for the CRM client card.
 *
 * Features (v2):
 *   • 8-second polling for /admin/whatsapp/messages/{id}
 *   • Textarea + Enter-to-send, with graceful 422 handling
 *   • 📋 Templates popover — 15+ canned replies with variable substitution
 *   • ✨ AI Suggest — Gemini drafts the next reply based on chat history
 *   • 🎙 Voice transcription — one-click OGG → text via Gemini
 */
export default function WhatsAppChat({ clientId, client, managerName, onDirtyChange,
                                        channel = "crm" }) {
  // `channel` переключает воронку: "crm" — клиенты бутика, "atelier" — заявки
  // ателье (второй Green API инстанс). Все эндпоинты зеркальные, поэтому
  // компонент один — правки автоматически работают в обеих воронках.
  const isAtelier = channel === "atelier";
  const BASE = isAtelier ? `${API}/admin/atelier/whatsapp` : `${API}/admin/whatsapp`;
  const draftKey = `wa_draft_${channel}_${clientId}`;
  const [messages, setMessages] = useState([]);
  // 24-часовое окно Cloud API: вне него Meta пропускает только шаблон.
  const [waWindow, setWaWindow] = useState(null);
  const [showTemplateSend, setShowTemplateSend] = useState(false);
  const windowClosed = !!(waWindow?.enforced && !waWindow?.open);
  const windowLeft = (() => {
    if (!waWindow?.enforced || !waWindow?.expires_at) return "";
    const ms = new Date(waWindow.expires_at) - new Date();
    if (ms <= 0) return "";
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    return h > 0 ? `${h} ч ${m} мин` : `${m} мин`;
  })();
  const [text, setText] = useState(() => {
    // Черновик переживает случайное закрытие карточки и перезагрузку
    // страницы на телефоне — набранный ответ больше не теряется.
    try { return localStorage.getItem(draftKey) || ""; }
    catch { return ""; }
  });
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [contacts, setContacts] = useState(null);
  const [recording, setRecording] = useState(false);
  const [recorded, setRecorded] = useState(null); // {blob, url, mime}
  const [sendingVoice, setSendingVoice] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [shareCategories, setShareCategories] = useState([]);
  const [shareBrands, setShareBrands] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [forwardingMsg, setForwardingMsg] = useState(null);
  const fileInputRef = useRef(null);
  useEffect(() => {
    // Lazy-load categories & brands once — needed by the ShareBuilder modal.
    axios.get(`${API}/categories`, { withCredentials: true })
      .then(r => setShareCategories(r.data || []))
      .catch(() => {});
    axios.get(`${API}/brands`, { withCredentials: true })
      .then(r => setShareBrands(r.data || []))
      .catch(() => {});
  }, []);
  const scrollerRef = useRef(null);
  const textareaRef = useRef(null);
  const lastLenRef = useRef(0);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const [recSeconds, setRecSeconds] = useState(0);
  const recTimerRef = useRef(null);

  // Load boutique contacts once — shown in a compact strip above the chat
  // so the manager doesn't have to leave the panel to grab the address.
  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/admin/knowledge-base/contacts`, { withCredentials: true })
      .then(r => { if (mounted) setContacts(r.data || {}); })
      .catch(() => { /* silently ignore — contacts strip is a nice-to-have */ });
    return () => { mounted = false; };
  }, []);

  // Bubble "dirty" state up so ClientDetail can prompt-before-close when
  // the manager clicks the backdrop by mistake.
  useEffect(() => {
    if (typeof onDirtyChange === "function") {
      onDirtyChange(!!text.trim() || recording || !!recorded);
    }
    try {
      if (text.trim()) localStorage.setItem(draftKey, text);
      else localStorage.removeItem(draftKey);
    } catch { /* приватный режим — просто без черновика */ }
  }, [text, recording, recorded, onDirtyChange, draftKey]);

  // Context used for template variable substitution.
  const templateCtx = useMemo(() => ({
    client_name: (client?.name || "").split(" ")[0] || "",
    product: "",
    price: "",
    city: client?.city || "",
    manager_name: managerName || "",
  }), [client, managerName]);

  const load = async () => {
    try {
      const r = await axios.get(`${BASE}/messages/${clientId}`,
                                 { withCredentials: true });
      const items = r.data.items || [];
      if (r.data.window) setWaWindow(r.data.window);
      // Обновляем состояние только если переписка реально изменилась:
      // иначе каждые 8 секунд шёл лишний ререндер, из-за которого на
      // телефоне «дёргалось» поле ввода во время набора ответа.
      setMessages(prev => {
        if (prev.length === items.length) {
          const same = prev.every((m, i) => m.id === items[i].id
            && m.status === items[i].status
            && m.transcript === items[i].transcript);
          if (same) return prev;
        }
        return items;
      });
    } catch (e) {
      setError(humanizeError(e, "Не удалось загрузить переписку"));
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
    // Intentionally exclude `load` — it's redefined every render and
    // would reset the interval before it fires. `clientId` is the only
    // input that should trigger re-polling.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  // Auto-scroll to bottom whenever new messages arrive.
  useEffect(() => {
    if (messages.length !== lastLenRef.current && scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
      lastLenRef.current = messages.length;
    }
  }, [messages]);

  const send = async () => {
    const t = text.trim();
    if (!t || sending) return;
    setSending(true);
    setError("");
    try {
      await axios.post(`${BASE}/send`,
                        isAtelier ? { order_id: clientId, text: t }
                                  : { client_id: clientId, text: t },
                        { withCredentials: true });
      setText("");
      try { localStorage.removeItem(draftKey); } catch { /* noop */ }
      load();
    } catch (e) {
      setError(humanizeError(e, "Не удалось отправить"));
    } finally { setSending(false); }
  };

  // Attach a photo / video / doc from the manager's device. Bytes go up
  // as multipart/form-data; backend streams them to R2 and hands the URL
  // to Green API. The optional caption piggy-backs on the current
  // textarea content so a manager can type a note first and then attach.
  const attachFile = async (fileObj) => {
    if (!fileObj || uploading) return;
    if (fileObj.size > 25 * 1024 * 1024) {
      setError("Файл больше 25 МБ — уменьшите размер");
      return;
    }
    setUploading(true);
    setUploadPct(0);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", fileObj);
      if (text.trim()) fd.append("caption", text.trim());
      await axios.post(`${BASE}/send-file/${clientId}`, fd, {
        withCredentials: true,
        onUploadProgress: (e) => {
          if (e.total) setUploadPct(Math.round((e.loaded * 100) / e.total));
        },
      });
      setText("");
      load();
    } catch (e) {
      setError(humanizeError(e, "Не удалось отправить файл"));
    } finally {
      setUploading(false);
      setUploadPct(0);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // AI drafts the next manager reply — result is INSERTED into the textarea
  // (not auto-sent) so the manager can edit before sending.
  const suggestReply = async () => {
    if (aiLoading) return;
    setAiLoading(true); setError("");
    try {
      const r = await axios.post(`${API}/admin/whatsapp/ai-suggest/${clientId}`,
                                   {}, { withCredentials: true });
      const draft = r.data?.text || "";
      if (draft) {
        setText(prev => prev ? `${prev}\n${draft}` : draft);
        // Refocus so the manager can immediately tweak the text.
        setTimeout(() => textareaRef.current?.focus(), 30);
      } else {
        setError("ИИ вернул пустой ответ");
      }
    } catch (e) {
      setError(humanizeError(e, "ИИ временно недоступен"));
    } finally { setAiLoading(false); }
  };

  const insertTemplate = (tpl) => {
    const filled = fillTemplate(tpl.text, templateCtx);
    setText(prev => prev ? `${prev}\n${filled}` : filled);
    setShowTemplates(false);
    setTimeout(() => textareaRef.current?.focus(), 30);
  };

  // Cache transcripts on the message doc so we hit Gemini once per note.
  const transcribeMessage = async (msgId) => {
    try {
      const r = await axios.post(`${BASE}/transcribe/${msgId}`,
                                   {}, { withCredentials: true });
      const t = r.data?.text || "";
      setMessages(prev => prev.map(m => m.id === msgId
        ? { ...m, audio_transcript: t } : m));
      return t;
    } catch (e) {
      setError(humanizeError(e, "Не удалось распознать голосовое"));
      throw e;
    }
  };

  // ─── Voice recording (MediaRecorder) ─────────────────────────────────
  // Chrome/Chromium → audio/webm;codecs=opus, Firefox → audio/ogg;codecs=opus,
  // Safari → audio/mp4. Backend re-encodes everything to WhatsApp OGG/Opus
  // via ffmpeg, so any of these are fine to record.
  const pickMimeType = () => {
    if (typeof window === "undefined" || !window.MediaRecorder) return "";
    const candidates = [
      "audio/webm;codecs=opus",
      "audio/ogg;codecs=opus",
      "audio/webm",
      "audio/mp4",
    ];
    for (const t of candidates) {
      if (window.MediaRecorder.isTypeSupported?.(t)) return t;
    }
    return "";
  };

  const startRecording = async () => {
    setError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = pickMimeType();
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime })
                       : new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || mime || "audio/webm" });
        const url = URL.createObjectURL(blob);
        setRecorded({ blob, url, mime: rec.mimeType || mime || "audio/webm" });
        // Release the mic — otherwise the browser shows the "recording" dot
        // even after the manager stopped talking.
        stream.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      };
      recorderRef.current = rec;
      rec.start();
      setRecording(true);
      setRecSeconds(0);
      recTimerRef.current = setInterval(() => setRecSeconds(s => {
        // Cap at 5 minutes — same limit the backend enforces on templates.
        if (s >= 300) { stopRecording(); return s; }
        return s + 1;
      }), 1000);
    } catch (e) {
      setError("Микрофон недоступен: " + (e?.message || "проверьте разрешения браузера"));
    }
  };

  const stopRecording = () => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
    if (recTimerRef.current) { clearInterval(recTimerRef.current); recTimerRef.current = null; }
    setRecording(false);
  };

  const cancelRecording = () => {
    stopRecording();
    if (recorded?.url) URL.revokeObjectURL(recorded.url);
    setRecorded(null);
    setRecSeconds(0);
  };

  const sendVoice = async () => {
    if (!recorded || sendingVoice) return;
    setSendingVoice(true); setError("");
    try {
      const fd = new FormData();
      const ext = (recorded.mime.includes("webm") ? "webm"
                    : recorded.mime.includes("ogg") ? "ogg"
                    : recorded.mime.includes("mp4") ? "mp4" : "webm");
      fd.append("audio", recorded.blob, `voice.${ext}`);
      await axios.post(`${BASE}/send-voice/${clientId}`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      URL.revokeObjectURL(recorded.url);
      setRecorded(null);
      setRecSeconds(0);
      load();
    } catch (e) {
      setError(humanizeError(e, "Не удалось отправить голосовое"));
    } finally { setSendingVoice(false); }
  };

  const saveAsVoiceTemplate = async (title) => {
    if (!recorded) return;
    const t = (title || "").trim();
    if (!t) { setError("Введите название шаблона"); return; }
    setSendingVoice(true); setError("");
    try {
      const fd = new FormData();
      const ext = (recorded.mime.includes("webm") ? "webm"
                    : recorded.mime.includes("ogg") ? "ogg"
                    : recorded.mime.includes("mp4") ? "mp4" : "webm");
      fd.append("audio", recorded.blob, `voice.${ext}`);
      fd.append("title", t);
      await axios.post(`${API}/admin/voice-templates`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      URL.revokeObjectURL(recorded.url);
      setRecorded(null);
      setRecSeconds(0);
    } catch (e) {
      setError(humanizeError(e, "Не удалось сохранить шаблон"));
    } finally { setSendingVoice(false); }
  };

  const sendVoiceTemplate = async (tplId) => {
    setSendingVoice(true); setError("");
    try {
      await axios.post(`${BASE}/send-voice-template/${tplId}/${clientId}`,
                        {}, { withCredentials: true });
      setShowTemplates(false);
      load();
    } catch (e) {
      setError(humanizeError(e, "Не удалось отправить голосовой шаблон"));
    } finally { setSendingVoice(false); }
  };

  return (
    <div data-testid="whatsapp-chat" style={{
      display: "flex", flexDirection: "column",
      border: "1px solid #e5e7eb", borderRadius: 8,
      background: "#fafaf9", overflow: "hidden", position: "relative",
    }}>
      <div style={{
        padding: "10px 14px", background: "#0A0A0A", color: "white",
        display: "flex", alignItems: "center", gap: 8,
        fontFamily: "Manrope", fontSize: 12, fontWeight: 600,
      }}>
        <MessageCircle size={14} style={{ color: "#25D366" }} />
        {isAtelier ? "WhatsApp ателье" : "WhatsApp-переписка"}
        <span style={{ marginLeft: "auto", fontSize: 10, color: "#8a8a89", fontWeight: 400 }}>
          автообновление · 8 сек
        </span>
      </div>

      {contacts && (contacts.address || contacts.phone || contacts.hours) && (
        <ContactStrip contacts={contacts} onInsert={(txt) => {
          setText(prev => prev ? `${prev}\n${txt}` : txt);
          setTimeout(() => textareaRef.current?.focus(), 30);
        }} />
      )}

      <div ref={scrollerRef} data-testid="whatsapp-chat-scroller"
        style={{ minHeight: 220, maxHeight: 380, overflowY: "auto",
                 padding: 14, display: "flex", flexDirection: "column", gap: 8 }}>
        {loading && messages.length === 0 && (
          <div style={{ textAlign: "center", color: "#8a8a89", fontFamily: "Manrope",
                        fontSize: 12, padding: 20 }}>
            <Loader2 size={16} className="animate-spin" style={{ display: "inline", marginRight: 6 }} />
            Загрузка…
          </div>
        )}
        {!loading && messages.length === 0 && (
          <div style={{ textAlign: "center", color: "#8a8a89", fontFamily: "Manrope",
                        fontSize: 12, padding: 30 }}>
            Ещё не переписывались через WhatsApp.
            <br />Напишите первое сообщение ниже.
          </div>
        )}
        {messages.map(m => <Bubble key={m.id} m={m} onTranscribe={transcribeMessage}
          onForward={() => setForwardingMsg(m)} />)}
      </div>

      {error && (
        <div style={{ padding: "6px 14px", background: "#fee2e2", color: "#7f1d1d",
                      fontSize: 11, fontFamily: "Manrope", borderTop: "1px solid #fecaca" }}>
          {error}
        </div>
      )}

      {/* Toolbar row: templates + AI suggest + curated share */}
      <div style={{ borderTop: "1px solid #e5e7eb", padding: "8px 10px",
                    background: "white", display: "flex", gap: 6, flexWrap: "wrap" }}>
        <ToolbarBtn data-testid="wa-open-templates" onClick={() => setShowTemplates(true)}
          title="Шаблоны быстрых ответов">
          <MessageSquareQuote size={13} /> Шаблоны
        </ToolbarBtn>
        {!isAtelier && (
          <ToolbarBtn data-testid="wa-ai-suggest" onClick={suggestReply} disabled={aiLoading}
            title="Пусть ИИ предложит ответ по контексту переписки"
            style={{ color: "#7A5A15", borderColor: "#F3E2B8", background: "#FDF6E8" }}>
            {aiLoading ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
            {aiLoading ? "ИИ думает…" : "Предложить ответ (ИИ)"}
          </ToolbarBtn>
        )}
        {!isAtelier && (
          <ToolbarBtn data-testid="wa-open-share" onClick={() => setShowShare(true)}
            title="Собрать подборку товаров и вставить ссылку в ответ"
            style={{ color: "#128C7E", borderColor: "#BEE3D5", background: "#EBF9F3" }}>
            <Gift size={13} /> Отправить подборку
          </ToolbarBtn>
        )}
      </div>

      {waWindow?.enforced && (
        windowClosed ? (
          <div data-testid="wa-window-closed"
            style={{ background: "#FFFBEB", borderTop: "1px solid #FDE68A",
                      borderBottom: "1px solid #FDE68A", padding: "9px 11px",
                      display: "flex", alignItems: "center",
                      justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
            <span style={{ fontFamily: "Manrope", fontSize: 12, color: "#78350F" }}>
              Клиент молчит больше 24 часов — свободный текст может не пройти
            </span>
            <button data-testid="wa-send-template-btn" onClick={() => setShowTemplateSend(true)}
              style={{ background: "#0A0A0A", color: "#D4B878", border: "1px solid #C5A059",
                        borderRadius: 6, padding: "7px 12px", cursor: "pointer",
                        fontFamily: "Manrope", fontSize: 12, fontWeight: 700 }}>
              Отправить шаблон
            </button>
          </div>
        ) : windowLeft ? (
          <div data-testid="wa-window-open"
            style={{ fontFamily: "Manrope", fontSize: 11.5, color: "#047857",
                      padding: "6px 11px 0", background: "white" }}>
            Свободный ответ доступен ещё {windowLeft}
          </div>
        ) : null
      )}

      <div style={{ padding: 10, background: "white", display: "flex", gap: 8 }}>
        <input ref={fileInputRef} type="file"
          data-testid="whatsapp-attach-input"
          accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx"
          style={{ display: "none" }}
          onChange={(e) => e.target.files?.[0] && attachFile(e.target.files[0])} />
        <button
          data-testid="whatsapp-attach-btn"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading || sending}
          title="Прикрепить фото, видео или файл"
          style={{
            background: uploading ? "#128C7E" : "#f5f5f4",
            border: "1px solid " + (uploading ? "#128C7E" : "#e5e7eb"),
            borderRadius: 6, padding: "0 12px",
            color: uploading ? "white" : "#3f3f46",
            cursor: (uploading || sending) ? "not-allowed" : "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            minWidth: 44, position: "relative",
          }}>
          {uploading ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              {uploadPct > 0 && (
                <span style={{ position: "absolute", top: -18, left: "50%",
                                transform: "translateX(-50%)", background: "#128C7E",
                                color: "white", fontSize: 10, padding: "1px 6px",
                                borderRadius: 4, fontFamily: "Manrope",
                                fontWeight: 700, whiteSpace: "nowrap" }}>
                  {uploadPct}%
                </span>
              )}
            </>
          ) : <Paperclip size={16} />}
        </button>
        <textarea
          ref={textareaRef}
          data-testid="whatsapp-chat-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
          }}
          placeholder="Ответ клиенту… (Enter — отправить, Shift+Enter — новая строка)"
          rows={2}
          style={{ flex: 1, resize: "none", border: "1px solid #e5e7eb",
                   borderRadius: 6, padding: "8px 10px", fontFamily: "Manrope",
                   fontSize: 13, outline: "none" }}
        />
        <button
          data-testid="whatsapp-mic-btn"
          onClick={recording ? stopRecording : startRecording}
          disabled={sendingVoice || !!recorded}
          title={recording ? "Остановить запись" : "Записать голосовое"}
          style={{
            background: recording ? "#dc2626" : "#f5f5f4",
            border: "1px solid " + (recording ? "#dc2626" : "#e5e7eb"),
            borderRadius: 6, padding: "0 12px",
            color: recording ? "white" : "#3f3f46",
            cursor: (sendingVoice || !!recorded) ? "not-allowed" : "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            minWidth: 44, position: "relative",
            opacity: (sendingVoice || !!recorded) ? 0.5 : 1,
          }}>
          {recording ? (
            <>
              <MicOff size={16} />
              <span style={{ position: "absolute", top: -18, left: "50%",
                              transform: "translateX(-50%)", background: "#dc2626",
                              color: "white", fontSize: 10, padding: "1px 6px",
                              borderRadius: 4, fontFamily: "Manrope", fontWeight: 700,
                              whiteSpace: "nowrap" }}>
                ● {formatDuration(recSeconds)}
              </span>
            </>
          ) : <Mic size={16} />}
        </button>
        <button
          data-testid="whatsapp-chat-send"
          onClick={send}
          disabled={!text.trim() || sending}
          title="Отправить"
          style={{
            background: (!text.trim() || sending) ? "#e5e7eb" :
              "linear-gradient(135deg, #25D366, #128C7E)",
            border: "none", borderRadius: 6, padding: "0 14px",
            color: "white", cursor: !text.trim() || sending ? "not-allowed" : "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            minWidth: 44,
          }}>
          {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
        </button>
      </div>

      {recorded && (
        <VoicePreviewModal
          recorded={recorded}
          sending={sendingVoice}
          onSend={sendVoice}
          onSaveTemplate={saveAsVoiceTemplate}
          onCancel={cancelRecording}
        />
      )}

      {showTemplateSend && (
        <MetaTemplateSend clientId={clientId} clientName={client?.name}
          onClose={() => setShowTemplateSend(false)} onSent={load} />
      )}

      {showTemplates && (
        <TemplatesPicker
          onPick={insertTemplate}
          onSendVoiceTemplate={sendVoiceTemplate}
          onClose={() => setShowTemplates(false)}
          templateCtx={templateCtx}
          initialCategory={isAtelier ? "atelier" : "all"}
        />
      )}

      {showShare && (
        <ShareBuilder
          categories={shareCategories}
          brands={shareBrands}
          onClose={() => setShowShare(false)}
          onInsert={(url) => {
            const draft = `${client?.name ? "Здравствуйте, " + client.name.split(" ")[0] + "! " : ""}` +
                          `Собрали для вас подборку — посмотрите, пожалуйста: ${url}`;
            setText(prev => prev ? `${prev}\n${draft}` : draft);
            setShowShare(false);
            setTimeout(() => textareaRef.current?.focus(), 30);
          }}
        />
      )}

      {forwardingMsg && (
        <ForwardModal
          msg={forwardingMsg}
          isAtelier={isAtelier}
          base={BASE}
          onClose={(sent) => {
            setForwardingMsg(null);
            if (sent) load();
          }}
        />
      )}
    </div>
  );
}

function formatDuration(sec) {
  const s = Math.max(0, Math.floor(sec || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/* ------------------------------------------------------------------- */
/*  Templates picker                                                     */
/* ------------------------------------------------------------------- */
function TemplatesPicker({ onPick, onSendVoiceTemplate, onClose, templateCtx,
                            initialCategory = "all" }) {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [voiceItems, setVoiceItems] = useState([]);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingVoice, setLoadingVoice] = useState(true);
  const [tab, setTab] = useState("text"); // "text" | "voice"
  const [filter, setFilter] = useState(initialCategory);
  const [query, setQuery] = useState("");
  const [sendingId, setSendingId] = useState("");
  // Inline editor state — swaps the list view for a create/edit form.
  const [editor, setEditor] = useState(null); // null | {id?, category, title, text}

  const loadVoice = async () => {
    setLoadingVoice(true);
    try {
      const r = await axios.get(`${API}/admin/voice-templates`, { withCredentials: true });
      setVoiceItems(r.data.items || []);
      setMe(r.data.me || null);
    } finally { setLoadingVoice(false); }
  };

  const loadText = async () => {
    try {
      const r = await axios.get(`${API}/admin/reply-templates`, { withCredentials: true });
      setItems(r.data.items || []);
      setCategories(r.data.categories || []);
      setMe(prev => prev || r.data.me || null);
    } finally { setLoading(false); }
  };

  useEffect(() => { loadText(); loadVoice(); }, []);

  // Ownership gates — mirror the admin page's rules so the picker never
  // shows a button the backend will refuse.
  const canEditTpl = (t) => {
    if (!me) return false;
    if (me.is_admin) return true;
    return !t.is_default;
  };
  const canDeleteTpl = (t) => {
    if (!me) return false;
    if (me.is_admin) return true;
    if (t.is_default) return false;
    return (t.created_by_id || "") === me.id;
  };

  const saveTpl = async () => {
    const e = editor;
    if (!e || !e.title.trim() || !e.text.trim()) return;
    try {
      if (e.id) {
        await axios.put(`${API}/admin/reply-templates/${e.id}`,
          { category: e.category, title: e.title, text: e.text },
          { withCredentials: true });
      } else {
        await axios.post(`${API}/admin/reply-templates`,
          { category: e.category, title: e.title, text: e.text },
          { withCredentials: true });
      }
      setEditor(null);
      loadText();
    } catch (err) {
      alert(err.response?.data?.detail || "Не удалось сохранить шаблон");
    }
  };

  const deleteTpl = async (t) => {
    if (!window.confirm(`Удалить шаблон «${t.title}»?`)) return;
    try {
      await axios.delete(`${API}/admin/reply-templates/${t.id}`, { withCredentials: true });
      loadText();
    } catch (err) {
      alert(err.response?.data?.detail || "Не удалось удалить");
    }
  };

  const q = query.trim().toLowerCase();
  const shown = items.filter(t => {
    if (filter !== "all" && t.category !== filter) return false;
    if (!q) return true;
    return (t.title.toLowerCase().includes(q) ||
            t.text.toLowerCase().includes(q));
  });
  const shownVoice = voiceItems.filter(v => !q || v.title.toLowerCase().includes(q));

  const handleSendVoice = async (v) => {
    setSendingId(v.id);
    try { await onSendVoiceTemplate?.(v.id); }
    finally { setSendingId(""); }
  };

  const removeVoice = async (v) => {
    if (!window.confirm(`Удалить голосовой шаблон «${v.title}»?`)) return;
    try {
      await axios.delete(`${API}/admin/voice-templates/${v.id}`, { withCredentials: true });
      loadVoice();
    } catch (e) {
      alert(e.response?.data?.detail || "Не удалось удалить");
    }
  };

  const canDeleteVoice = (v) => {
    if (!me) return false;
    if (me.is_admin) return true;
    return (v.created_by_id || "") === me.id;
  };

  return createPortal(
    <div onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(10,10,10,0.55)",
               zIndex: 1200, display: "flex", alignItems: "center",
               justifyContent: "center", padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()}
        data-testid="wa-templates-picker"
        style={{ background: "white", borderRadius: 10, width: 640, maxWidth: "100%",
                 maxHeight: "80vh", display: "flex", flexDirection: "column",
                 overflow: "hidden", boxShadow: "0 24px 60px rgba(0,0,0,0.35)" }}>
        <div style={{ padding: "14px 18px", borderBottom: "1px solid #e5e7eb",
                      display: "flex", alignItems: "center", gap: 10 }}>
          <MessageSquareQuote size={16} style={{ color: "#C5A059" }} />
          <div style={{ fontFamily: "Manrope", fontWeight: 700, fontSize: 15 }}>
            {editor ? (editor.id ? "Редактировать шаблон" : "Новый шаблон") : "Быстрые ответы"}
          </div>
          {!editor && (
            <div style={{ marginLeft: "auto", position: "relative" }}>
              <Search size={13} style={{ position: "absolute", left: 8, top: 9, color: "#8a8a89" }} />
              <input data-testid="wa-templates-search" value={query} onChange={e => setQuery(e.target.value)}
                placeholder="Поиск шаблона…"
                style={{ paddingLeft: 26, padding: "6px 10px 6px 26px", border: "1px solid #e5e7eb",
                         borderRadius: 6, fontFamily: "Manrope", fontSize: 12, outline: "none",
                         width: 200 }} autoFocus />
            </div>
          )}
          {!editor && tab === "text" && (
            <button data-testid="wa-tpl-new"
              onClick={() => setEditor({ category: categories[0]?.key || "other", title: "", text: "" })}
              title="Создать новый шаблон"
              style={{ display: "flex", alignItems: "center", gap: 4,
                       background: "linear-gradient(135deg,#C5A059,#D4B878)", color: "#0A0A0A",
                       border: "none", borderRadius: 6, padding: "6px 10px",
                       fontFamily: "Manrope", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
              <Plus size={12} /> Новый
            </button>
          )}
          <button onClick={editor ? () => setEditor(null) : onClose}
            style={{ background: "none", border: "none", cursor: "pointer",
                     color: "#8a8a89" }}>
            <X size={18} />
          </button>
        </div>

        {editor ? (
          <TemplateEditor editor={editor} setEditor={setEditor} categories={categories}
            onSave={saveTpl} onCancel={() => setEditor(null)} />
        ) : (
        <>
        {/* Text / Voice tabs */}
        <div style={{ display: "flex", borderBottom: "1px solid #e5e7eb", background: "white" }}>
          <TabBtn active={tab === "text"} onClick={() => setTab("text")}
            data-testid="wa-tab-text">
            <MessageSquareQuote size={13} /> Текстовые ({items.length})
          </TabBtn>
          <TabBtn active={tab === "voice"} onClick={() => setTab("voice")}
            data-testid="wa-tab-voice">
            <Mic size={13} /> Голосовые ({voiceItems.length})
          </TabBtn>
        </div>

        {tab === "text" && (
          <div style={{ padding: "10px 18px", display: "flex", gap: 6, flexWrap: "wrap",
                        borderBottom: "1px solid #f5f5f4", background: "#fafaf9" }}>
            <FilterChip active={filter === "all"} onClick={() => setFilter("all")}>
              Все ({items.length})
            </FilterChip>
            {categories.map(c => {
              const count = items.filter(t => t.category === c.key).length;
              if (count === 0) return null;
              return <FilterChip key={c.key} active={filter === c.key} onClick={() => setFilter(c.key)}>
                {c.label} ({count})
              </FilterChip>;
            })}
          </div>
        )}

        {tab === "voice" ? (
          <div style={{ overflowY: "auto", flex: 1, padding: 14 }}>
            {loadingVoice && (
              <div style={{ padding: 20, textAlign: "center", color: "#8a8a89",
                             fontFamily: "Manrope", fontSize: 12 }}>Загрузка…</div>
            )}
            {!loadingVoice && shownVoice.length === 0 && (
              <div style={{ padding: 30, textAlign: "center", color: "#8a8a89",
                             fontFamily: "Manrope", fontSize: 12 }}>
                <Mic size={22} style={{ opacity: 0.4, marginBottom: 8 }} />
                <div>Ещё нет голосовых шаблонов.</div>
                <div style={{ marginTop: 6, fontSize: 11 }}>
                  Запишите голосовое кнопкой микрофона и сохраните как шаблон
                  — оно появится здесь.
                </div>
              </div>
            )}
            <div style={{ display: "grid", gap: 8 }}>
              {shownVoice.map(v => (
                <div key={v.id} data-testid={`wa-voice-tpl-${v.id}`}
                  style={{ background: "white", border: "1px solid #e5e7eb",
                           borderRadius: 8, padding: "10px 12px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                    <Mic size={13} style={{ color: "#C5A059", flexShrink: 0 }} />
                    <span style={{ fontFamily: "Manrope", fontWeight: 600, fontSize: 13, color: "#0A0A0A", flex: 1 }}>
                      {v.title}
                    </span>
                    <span style={{ fontFamily: "Manrope", fontSize: 10, color: "#8a8a89" }}>
                      {formatDuration(v.duration_sec)}
                    </span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <audio controls preload="none" src={v.url}
                      style={{ flex: 1, height: 30, minWidth: 0 }} />
                    <button data-testid={`wa-voice-send-${v.id}`}
                      onClick={() => handleSendVoice(v)}
                      disabled={sendingId === v.id}
                      title="Отправить клиенту"
                      style={{ background: sendingId === v.id ? "#e5e7eb" :
                                          "linear-gradient(135deg, #25D366, #128C7E)",
                               color: "white", border: "none", borderRadius: 6,
                               padding: "6px 10px", cursor: sendingId === v.id ? "wait" : "pointer",
                               display: "flex", alignItems: "center", gap: 4,
                               fontFamily: "Manrope", fontSize: 11, fontWeight: 700 }}>
                      {sendingId === v.id ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                      Отправить
                    </button>
                    {canDeleteVoice(v) && (
                      <button onClick={() => removeVoice(v)} title="Удалить"
                        data-testid={`wa-voice-delete-${v.id}`}
                        style={{ background: "white", border: "1px solid #e5e7eb",
                                 borderRadius: 6, padding: "6px 8px", cursor: "pointer",
                                 color: "#dc2626" }}>
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                  {v.created_by_name && (
                    <div style={{ marginTop: 6, fontFamily: "Manrope", fontSize: 10, color: "#8a8a89" }}>
                      Автор: {v.created_by_name}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div style={{ overflowY: "auto", flex: 1, padding: 14 }}>
            {loading && <div style={{ padding: 20, textAlign: "center", color: "#8a8a89",
                                       fontFamily: "Manrope", fontSize: 12 }}>Загрузка…</div>}
            {!loading && shown.length === 0 && (
              <div style={{ padding: 20, textAlign: "center", color: "#8a8a89",
                            fontFamily: "Manrope", fontSize: 12 }}>
                Ничего не найдено.
              </div>
            )}
            <div style={{ display: "grid", gap: 6 }}>
              {shown.map(t => {
                const preview = fillTemplate(t.text, templateCtx);
                const showEdit = canEditTpl(t);
                const showDelete = canDeleteTpl(t);
                return (
                  <div key={t.id} data-testid={`wa-tpl-row-${t.id}`}
                    style={{ background: "white", border: "1px solid #e5e7eb",
                             borderRadius: 8, display: "flex", overflow: "hidden",
                             transition: "border-color 0.15s, background 0.15s" }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#C5A059";
                                            e.currentTarget.style.background = "#fdfbf5"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e5e7eb";
                                            e.currentTarget.style.background = "white"; }}>
                    <button data-testid={`wa-tpl-${t.id}`}
                      onClick={() => onPick(t)}
                      style={{ textAlign: "left", background: "transparent", border: "none",
                               flex: 1, padding: "10px 12px", cursor: "pointer",
                               display: "flex", flexDirection: "column", gap: 4 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 9, letterSpacing: "0.12em", textTransform: "uppercase",
                                        color: "#C5A059", fontWeight: 700, fontFamily: "Manrope" }}>
                          {t.category_label}
                        </span>
                        <span style={{ fontFamily: "Manrope", fontWeight: 600, fontSize: 13, color: "#0A0A0A" }}>
                          {t.title}
                        </span>
                        {t.is_default && (
                          <span style={{ fontSize: 9, color: "#8a8a89", background: "#f5f5f4",
                                          padding: "1px 5px", borderRadius: 3 }}>
                            дефолт
                          </span>
                        )}
                      </div>
                      <div style={{ fontFamily: "Manrope", fontSize: 12, color: "#3f3f46",
                                    whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
                        {preview}
                      </div>
                    </button>
                    {(showEdit || showDelete) && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 2,
                                     padding: 6, borderLeft: "1px solid #f5f5f4",
                                     background: "#fafaf9" }}>
                        {showEdit && (
                          <button data-testid={`wa-tpl-edit-${t.id}`}
                            onClick={(e) => { e.stopPropagation();
                              setEditor({ id: t.id, category: t.category, title: t.title, text: t.text }); }}
                            title="Редактировать"
                            style={{ background: "white", border: "1px solid #e5e7eb",
                                     borderRadius: 5, padding: "4px 6px", cursor: "pointer",
                                     color: "#3f3f46", display: "flex" }}>
                            <Edit2 size={11} />
                          </button>
                        )}
                        {showDelete && (
                          <button data-testid={`wa-tpl-delete-${t.id}`}
                            onClick={(e) => { e.stopPropagation(); deleteTpl(t); }}
                            title="Удалить"
                            style={{ background: "white", border: "1px solid #e5e7eb",
                                     borderRadius: 5, padding: "4px 6px", cursor: "pointer",
                                     color: "#dc2626", display: "flex" }}>
                            <Trash2 size={11} />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        </>
        )}
      </div>
    </div>,
    document.body,
  );
}

function FilterChip({ active, onClick, children }) {
  return (
    <button onClick={onClick}
      style={{ padding: "4px 10px", borderRadius: 999, border: "1px solid",
               borderColor: active ? "#C5A059" : "#e5e7eb",
               background: active ? "rgba(197,160,89,0.12)" : "white",
               color: active ? "#7A5A15" : "#3f3f46",
               fontFamily: "Manrope", fontSize: 11, fontWeight: 500, cursor: "pointer" }}>
      {children}
    </button>
  );
}

// Inline editor for text templates — replaces the list view when active.
function TemplateEditor({ editor, setEditor, categories, onSave, onCancel }) {
  const canSave = !!(editor.title.trim() && editor.text.trim());
  return (
    <div style={{ padding: 18, display: "flex", flexDirection: "column", gap: 14,
                   overflowY: "auto", flex: 1 }}>
      <label style={{ display: "block" }}>
        <div style={editorLabel}>Категория</div>
        <select data-testid="wa-tpl-editor-category" value={editor.category}
          onChange={(e) => setEditor({ ...editor, category: e.target.value })}
          style={editorInput}>
          {categories.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
      </label>
      <label style={{ display: "block" }}>
        <div style={editorLabel}>Название</div>
        <input data-testid="wa-tpl-editor-title" value={editor.title}
          onChange={(e) => setEditor({ ...editor, title: e.target.value })}
          placeholder="Например: Приглашение на примерку"
          style={editorInput} autoFocus />
      </label>
      <label style={{ display: "block" }}>
        <div style={editorLabel}>
          Текст сообщения
          <span style={{ color: "#8a8a89", fontWeight: 400, textTransform: "none",
                          letterSpacing: 0, marginLeft: 6, fontSize: 10 }}>
            · переменные: {"{client_name}"}, {"{product}"}, {"{price}"}
          </span>
        </div>
        <textarea data-testid="wa-tpl-editor-text" value={editor.text}
          onChange={(e) => setEditor({ ...editor, text: e.target.value })}
          placeholder="Здравствуйте, {client_name}! …"
          rows={7}
          style={{ ...editorInput, resize: "vertical", lineHeight: 1.55,
                    fontFamily: "Manrope" }} />
      </label>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8,
                     borderTop: "1px solid #f5f5f4", paddingTop: 14 }}>
        <button onClick={onCancel}
          style={{ background: "#f5f5f4", border: "1px solid #e5e7eb", borderRadius: 6,
                   padding: "9px 16px", cursor: "pointer", fontFamily: "Manrope",
                   fontSize: 12 }}>
          Отмена
        </button>
        <button data-testid="wa-tpl-editor-save" onClick={onSave} disabled={!canSave}
          style={{ background: canSave ? "linear-gradient(135deg,#C5A059,#D4B878)" : "#e5e7eb",
                   color: "#0A0A0A", border: "none", borderRadius: 6, padding: "9px 18px",
                   cursor: canSave ? "pointer" : "not-allowed",
                   fontFamily: "Manrope", fontSize: 12, fontWeight: 700,
                   display: "flex", alignItems: "center", gap: 6 }}>
          <Save size={13} /> Сохранить
        </button>
      </div>
    </div>
  );
}
const editorLabel = {
  fontFamily: "Manrope", fontSize: 10, fontWeight: 700, color: "#0A0A0A",
  textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 5,
};
const editorInput = {
  width: "100%", padding: "9px 12px", border: "1px solid #e5e7eb",
  borderRadius: 6, fontFamily: "Manrope", fontSize: 13, outline: "none",
  background: "white", boxSizing: "border-box",
};

function TabBtn({ active, onClick, children, ...props }) {  return (
    <button onClick={onClick} {...props}
      style={{
        flex: 1, padding: "10px 14px", border: "none", background: "transparent",
        borderBottom: "2px solid " + (active ? "#C5A059" : "transparent"),
        color: active ? "#7A5A15" : "#6b7280", cursor: "pointer",
        display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
        fontFamily: "Manrope", fontSize: 12, fontWeight: 600,
        transition: "border-color 0.15s, color 0.15s",
      }}>
      {children}
    </button>
  );
}

/**
 * Modal shown right after the manager stops recording. Lets them preview,
 * discard, send, or save to the shared voice-template library.
 */
function VoicePreviewModal({ recorded, sending, onSend, onSaveTemplate, onCancel }) {
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState("");
  const [mode, setMode] = useState("preview"); // "preview" | "save"

  const handleSave = async () => {
    setSaving(true);
    try { await onSaveTemplate?.(title); }
    finally { setSaving(false); }
  };

  return createPortal(
    <div style={{ position: "fixed", inset: 0, background: "rgba(10,10,10,0.6)",
                   zIndex: 1250, display: "flex", alignItems: "center",
                   justifyContent: "center", padding: 20 }}>
      <div data-testid="wa-voice-preview"
        style={{ background: "white", borderRadius: 10, padding: 22, width: 460,
                 maxWidth: "100%", boxShadow: "0 24px 60px rgba(0,0,0,0.35)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
          <Volume2 size={18} style={{ color: "#25D366" }} />
          <div style={{ fontFamily: "Manrope", fontWeight: 700, fontSize: 15 }}>
            {mode === "save" ? "Сохранить как шаблон" : "Готово — прослушайте запись"}
          </div>
          <button onClick={onCancel} disabled={sending || saving}
            style={{ marginLeft: "auto", background: "none", border: "none",
                     cursor: "pointer", color: "#8a8a89" }}>
            <X size={18} />
          </button>
        </div>

        <audio controls src={recorded.url} autoPlay={false}
          style={{ width: "100%", marginBottom: 16 }} />

        {mode === "save" && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontFamily: "Manrope", fontSize: 10, fontWeight: 700,
                           color: "#0A0A0A", textTransform: "uppercase",
                           letterSpacing: "0.1em", marginBottom: 5 }}>
              Название шаблона
            </div>
            <input data-testid="wa-voice-template-title" value={title}
              onChange={e => setTitle(e.target.value)} autoFocus
              placeholder="Например: Приветствие"
              style={{ width: "100%", padding: "9px 12px", border: "1px solid #e5e7eb",
                       borderRadius: 6, fontFamily: "Manrope", fontSize: 13,
                       outline: "none", boxSizing: "border-box" }} />
          </div>
        )}

        <div style={{ display: "flex", gap: 8 }}>
          {mode === "preview" ? (
            <>
              <button data-testid="wa-voice-discard" onClick={onCancel} disabled={sending}
                style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 6,
                         padding: "10px 14px", cursor: "pointer", color: "#dc2626",
                         fontFamily: "Manrope", fontSize: 12, fontWeight: 600,
                         display: "flex", alignItems: "center", gap: 6 }}>
                <Trash2 size={13} /> Удалить
              </button>
              <button data-testid="wa-voice-save-tpl" onClick={() => setMode("save")}
                disabled={sending}
                style={{ background: "#f5f5f4", border: "1px solid #e5e7eb", borderRadius: 6,
                         padding: "10px 14px", cursor: "pointer", color: "#3f3f46",
                         fontFamily: "Manrope", fontSize: 12, fontWeight: 600,
                         display: "flex", alignItems: "center", gap: 6 }}>
                <Save size={13} /> В шаблоны
              </button>
              <button data-testid="wa-voice-send" onClick={onSend} disabled={sending}
                style={{ marginLeft: "auto",
                         background: sending ? "#e5e7eb" :
                                       "linear-gradient(135deg, #25D366, #128C7E)",
                         border: "none", borderRadius: 6, padding: "10px 18px",
                         color: "white", cursor: sending ? "wait" : "pointer",
                         fontFamily: "Manrope", fontSize: 12, fontWeight: 700,
                         display: "flex", alignItems: "center", gap: 6 }}>
                {sending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                {sending ? "Отправка…" : "Отправить клиенту"}
              </button>
            </>
          ) : (
            <>
              <button onClick={() => setMode("preview")} disabled={saving}
                style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 6,
                         padding: "10px 14px", cursor: "pointer", color: "#3f3f46",
                         fontFamily: "Manrope", fontSize: 12, fontWeight: 600 }}>
                Назад
              </button>
              <button data-testid="wa-voice-save-confirm" onClick={handleSave}
                disabled={saving || !title.trim()}
                style={{ marginLeft: "auto",
                         background: (saving || !title.trim()) ? "#e5e7eb" :
                                       "linear-gradient(135deg, #C5A059, #D4B878)",
                         border: "none", borderRadius: 6, padding: "10px 18px",
                         color: "#0A0A0A", cursor: (saving || !title.trim()) ? "not-allowed" : "pointer",
                         fontFamily: "Manrope", fontSize: 12, fontWeight: 700,
                         display: "flex", alignItems: "center", gap: 6 }}>
                {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                Сохранить шаблон
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function ToolbarBtn({ children, style, ...props }) {
  return (
    <button {...props}
      style={{ display: "flex", alignItems: "center", gap: 6,
               background: "#f5f5f4", border: "1px solid #e5e7eb", borderRadius: 6,
               padding: "5px 10px", fontFamily: "Manrope", fontSize: 12, fontWeight: 500,
               color: "#3f3f46", cursor: props.disabled ? "wait" : "pointer",
               opacity: props.disabled ? 0.6 : 1, ...style }}>
      {children}
    </button>
  );
}

/* ------------------------------------------------------------------- */
/*  Bubble + media rendering                                             */
/* ------------------------------------------------------------------- */
function Bubble({ m, onTranscribe, onForward }) {
  const isIn = m.direction === "in";
  const time = m.created_at ? new Date(m.created_at) : null;
  const timeStr = time
    ? time.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" })
      + " · " + time.toLocaleDateString("ru-RU", { day: "2-digit", month: "short" })
    : "";
  const hasMedia = !!m.media_type;
  const caption = hasMedia && m.text && !/^\[.+\]$/.test(m.text) ? m.text : "";
  const legacyMedia = !hasMedia && LEGACY_MEDIA_LABELS[m.text];
  // Read/delivered indicators — WhatsApp-style ticks. Only for outbound.
  const ticks = !isIn ? statusTicks(m.provider_status) : null;
  const fromPhone = !isIn && m.sent_from_phone;
  const isAI = !isIn && m.sent_by_ai;

  return (
    <div style={{ display: "flex", justifyContent: isIn ? "flex-start" : "flex-end" }}>
      <div style={{
        maxWidth: "82%",
        background: isIn ? "white" : "linear-gradient(135deg, #DCF8C6, #B2E999)",
        border: isIn ? "1px solid #e5e7eb" : "1px solid #a5d68f",
        borderRadius: isIn ? "12px 12px 12px 2px" : "12px 12px 2px 12px",
        padding: hasMedia ? "6px 6px 8px" : "8px 12px",
        fontFamily: "Manrope",
        fontSize: 13,
        color: "#0A0A0A",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
        overflow: "hidden",
      }}>
        {hasMedia ? (
          <MediaContent m={m} onTranscribe={onTranscribe} />
        ) : legacyMedia ? (
          <LegacyMediaNote label={legacyMedia} />
        ) : (
          <div>{m.text}</div>
        )}
        {caption && (
          <div style={{ padding: hasMedia ? "6px 6px 0" : 0, whiteSpace: "pre-wrap" }}>
            {caption}
          </div>
        )}
        <div style={{ fontSize: 10, color: "#64748b", marginTop: 4, textAlign: "right",
                      padding: hasMedia ? "0 6px" : 0,
                      display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 4 }}>
          {onForward && (
            <button
              data-testid={`msg-forward-${m.id}`}
              onClick={() => onForward(m)}
              title="Переслать другому клиенту"
              style={{ background: "transparent", border: "none", cursor: "pointer",
                        padding: 2, color: "#128C7E", display: "inline-flex",
                        alignItems: "center", marginRight: "auto" }}>
              <Forward size={12} />
            </button>
          )}
          {m.forwarded_from_msg_id && (
            <span data-testid="msg-forwarded-badge"
              style={{ background: "rgba(37,211,102,0.15)", color: "#128C7E",
                       borderRadius: 3, padding: "1px 5px", fontSize: 9, fontWeight: 700 }}
              title="Переслано">
              ↩️ переслано
            </span>
          )}
          {fromPhone && (
            <span data-testid="msg-from-phone"
              style={{ background: "rgba(37,211,102,0.15)", color: "#128C7E",
                       borderRadius: 3, padding: "1px 5px", fontSize: 9, fontWeight: 700 }}
              title="Менеджер ответил с телефона">
              📱 с телефона
            </span>
          )}
          {isAI && (
            <span data-testid="msg-sent-by-ai"
              style={{ background: "linear-gradient(135deg,#C5A059,#D4B878)",
                       color: "#0A0A0A", borderRadius: 3, padding: "1px 5px",
                       fontSize: 9, fontWeight: 800, letterSpacing: 0.5 }}
              title="Автоответ Autopilot (AI)">
              🤖 AI
            </span>
          )}
          {!isIn && !fromPhone && m.sent_by_name ? <span>{m.sent_by_name}</span> : null}
          <span>{timeStr}</span>
          {ticks && (
            <span data-testid={`msg-status-${m.provider_status}`}
              style={{ color: ticks.color, fontWeight: 700, fontFamily: "monospace" }}
              title={ticks.label}>
              {ticks.icon}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// Recognized "raw type" strings that legacy Phase-1 webhooks stored as
// text (before we shipped `_extract_message_content`). Everything on this
// list gets a friendlier placeholder in the UI.
const LEGACY_MEDIA_LABELS = {
  "[imageMessage]":     "Фото",
  "[videoMessage]":     "Видео",
  "[audioMessage]":     "Голосовое",
  "[pttMessage]":       "Голосовое",
  "[documentMessage]":  "Документ",
  "[stickerMessage]":   "Стикер",
  "[locationMessage]":  "Геолокация",
};

// WhatsApp-style ticks for outbound messages. Grey ✓ for sent, grey ✓✓
// for delivered to device, BLUE ✓✓ for read. Failed = red exclamation.
function statusTicks(status) {
  switch ((status || "").toLowerCase()) {
    case "sent":      return { icon: "✓",  color: "#94a3b8", label: "Отправлено" };
    case "delivered": return { icon: "✓✓", color: "#94a3b8", label: "Доставлено" };
    case "read":      return { icon: "✓✓", color: "#0EA5E9", label: "Прочитано" };
    case "played":    return { icon: "✓✓", color: "#0EA5E9", label: "Прослушано" };
    case "failed":    return { icon: "⚠",  color: "#dc2626", label: "Ошибка отправки" };
    case "noaccount": return { icon: "⚠",  color: "#dc2626", label: "Нет WhatsApp" };
    default: return null;
  }
}

function LegacyMediaNote({ label }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8,
                   padding: "8px 10px",
                   background: "rgba(255,255,255,0.5)",
                   border: "1px dashed #d4d4d4", borderRadius: 6,
                   color: "#8a8a89", fontStyle: "italic" }}>
      <FileText size={14} style={{ flexShrink: 0 }} />
      <div>
        <div style={{ color: "#3f3f46", fontStyle: "normal", fontWeight: 600 }}>
          {label}
        </div>
        <div style={{ fontSize: 10, marginTop: 1 }}>
          файл больше недоступен — прислано до обновления
        </div>
      </div>
    </div>
  );
}

function MediaContent({ m, onTranscribe }) {
  const url = m.media_url;
  const thumb = m.media_thumb ? `data:image/jpeg;base64,${m.media_thumb}` : null;
  const [zoom, setZoom] = useState(false);

  if (m.media_type === "image") {
    if (!url && !thumb) return <FallbackNote label="фото недоступно" />;
    return (
      <>
        <img
          src={url || thumb}
          alt="whatsapp фото"
          onClick={url ? () => setZoom(true) : undefined}
          style={{
            display: "block", width: "100%", maxHeight: 260,
            objectFit: "cover", borderRadius: 8, cursor: url ? "zoom-in" : "default",
            background: "#f5f5f4",
          }}
          onError={(e) => { if (thumb && e.currentTarget.src !== thumb) e.currentTarget.src = thumb; }}
        />
        {zoom && url && <ImageZoom src={url} onClose={() => setZoom(false)} />}
      </>
    );
  }

  if (m.media_type === "video") {
    if (!url) return <FallbackNote label="видео недоступно" />;
    return (
      <video controls preload="metadata"
        poster={thumb || undefined}
        style={{ display: "block", width: "100%", maxHeight: 260,
                 borderRadius: 8, background: "#000" }}>
        <source src={url} type={m.media_mime || "video/mp4"} />
        Ваш браузер не поддерживает воспроизведение видео.
      </video>
    );
  }

  if (m.media_type === "audio") {
    if (!url) return <FallbackNote label="голосовое недоступно" icon={<Mic size={13} />} />;
    return <VoiceMessage m={m} url={url} onTranscribe={onTranscribe} />;
  }

  const filename = m.media_filename || "документ";
  const mime = m.media_mime || "";
  return (
    <a href={url || "#"} target="_blank" rel="noopener noreferrer"
      onClick={(e) => { if (!url) e.preventDefault(); }}
      style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "10px 12px", background: "#fafaf9",
        border: "1px solid #e5e7eb", borderRadius: 8,
        color: "#0A0A0A", textDecoration: "none",
        cursor: url ? "pointer" : "default",
      }}>
      <FileText size={22} style={{ color: "#128C7E", flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, overflow: "hidden",
                      textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {filename}
        </div>
        <div style={{ fontSize: 11, color: "#8a8a89", marginTop: 2 }}>
          {mime || (url ? "открыть" : "недоступно")}
        </div>
      </div>
      {url && <span style={{ fontSize: 11, color: "#128C7E", fontWeight: 600 }}>↗</span>}
    </a>
  );
}

/**
 * Voice note bubble — plays the OGG file and offers a "Показать текст"
 * action that calls the Gemini transcription endpoint. Result is cached
 * on the message, so subsequent viewings are instant.
 */
function VoiceMessage({ m, url, onTranscribe }) {
  const [loading, setLoading] = useState(false);
  const [transcript, setTranscript] = useState(m.audio_transcript || "");
  const [expanded, setExpanded] = useState(!!m.audio_transcript);
  const [error, setError] = useState("");

  const handle = async () => {
    if (transcript) { setExpanded(v => !v); return; }
    setLoading(true); setError("");
    try {
      const t = await onTranscribe(m.id);
      setTranscript(t); setExpanded(true);
    } catch (e) {
      setError("не удалось распознать");
    } finally { setLoading(false); }
  };

  return (
    <div style={{ padding: "6px 8px", display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Mic size={16} style={{ color: "#128C7E", flexShrink: 0 }} />
        <audio controls preload="none" src={url}
          style={{ height: 32, flex: 1, minWidth: 0, maxWidth: 260 }}>
          Ваш браузер не поддерживает воспроизведение аудио.
        </audio>
      </div>
      <button data-testid={`wa-transcribe-${m.id}`} onClick={handle}
        style={{ alignSelf: "flex-start", display: "flex", alignItems: "center", gap: 4,
                 background: "transparent", border: "none", cursor: "pointer",
                 color: "#7A5A15", fontFamily: "Manrope", fontSize: 11,
                 padding: "2px 0", textDecoration: "underline" }}>
        {loading ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
        {loading ? "распознаю…"
                 : transcript ? (expanded ? "скрыть текст" : "показать текст")
                              : "распознать голос"}
      </button>
      {expanded && transcript && (
        <div style={{ background: "#fffdf5", border: "1px solid #F3E2B8", borderRadius: 6,
                       padding: "8px 10px", fontFamily: "Manrope", fontSize: 12,
                       color: "#3f3f46", lineHeight: 1.5, fontStyle: "italic" }}>
          «{transcript}»
        </div>
      )}
      {error && (
        <div style={{ color: "#b91c1c", fontFamily: "Manrope", fontSize: 11 }}>{error}</div>
      )}
    </div>
  );
}

function FallbackNote({ label, icon }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px",
                  background: "#fafaf9", border: "1px dashed #d4d4d4", borderRadius: 8,
                  color: "#8a8a89", fontSize: 12, fontStyle: "italic" }}>
      {icon} {label} — ссылка Green API истекла
    </div>
  );
}

function ImageZoom({ src, onClose }) {
  return createPortal(
    <div onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.92)",
               zIndex: 1300, display: "flex", alignItems: "center",
               justifyContent: "center", padding: 24, cursor: "zoom-out" }}>
      <img src={src} alt="фото"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: "95vw", maxHeight: "90vh", objectFit: "contain",
                 borderRadius: 6, background: "#111" }} />
    </div>,
    document.body,
  );
}

/**
 * Curated-share picker — one-tap way to pick a category and generate a
 * public share link the client can open on their phone. The manager gets
 * the URL inserted into the WhatsApp textarea; they can still edit the
 * outer message before hitting Send.
 *
 * Flow: pick category → click «Собрать подборку» → backend suggests up to
 * 5 items in that category → creates a bundle → returns the token → we
 * insert the URL into the chat.
 */
function SharePickerModal({ clientName, onInsert, onCancel }) {
  const [categories, setCategories] = useState([]);
  const [categorySlug, setCategorySlug] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/categories`, { withCredentials: true })
      .then(r => setCategories(r.data || []))
      .catch(() => { /* categories are optional — filter still works without */ });
  }, []);

  const suggest = async () => {
    setError(""); setLoading(true); setSuggestions([]);
    try {
      const body = {};
      if (categorySlug) body.category_slug = categorySlug;
      if (priceMax) body.price_max = parseFloat(priceMax);
      const r = await axios.post(`${API}/admin/shares/suggest`, body,
                                   { withCredentials: true });
      const items = r.data?.items || [];
      if (!items.length) {
        setError("По этим фильтрам ничего не нашлось");
        return;
      }
      setSuggestions(items);
    } catch (e) {
      setError(e.response?.data?.detail || "Не удалось получить подборку");
    } finally { setLoading(false); }
  };

  const createAndInsert = async () => {
    if (!suggestions.length) return;
    setLoading(true); setError("");
    try {
      const payload = {
        product_ids: suggestions.map(s => s.id),
        category_slug: categorySlug || null,
        price_min: null,
        price_max: priceMax ? parseFloat(priceMax) : null,
      };
      const r = await axios.post(`${API}/admin/shares`, payload,
                                   { withCredentials: true });
      const url = r.data?.public_url || r.data?.url;
      if (!url) throw new Error("Ссылка не получена");
      onInsert(url);
    } catch (e) {
      setError(e.response?.data?.detail || "Не удалось создать ссылку");
    } finally { setLoading(false); }
  };

  return createPortal(
    <div onClick={onCancel}
      style={{ position: "fixed", inset: 0, background: "rgba(10,10,10,0.55)",
               zIndex: 1200, display: "flex", alignItems: "center",
               justifyContent: "center", padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()}
        data-testid="wa-share-picker"
        style={{ background: "white", borderRadius: 10, width: 520, maxWidth: "100%",
                 maxHeight: "80vh", display: "flex", flexDirection: "column",
                 overflow: "hidden", boxShadow: "0 24px 60px rgba(0,0,0,0.35)" }}>
        <div style={{ padding: "14px 18px", borderBottom: "1px solid #e5e7eb",
                      display: "flex", alignItems: "center", gap: 10 }}>
          <Gift size={16} style={{ color: "#128C7E" }} />
          <div style={{ fontFamily: "Manrope", fontWeight: 700, fontSize: 15 }}>
            Собрать подборку для клиента
          </div>
          <button onClick={onCancel} style={{ marginLeft: "auto", background: "none",
                                                border: "none", cursor: "pointer",
                                                color: "#8a8a89" }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: 18, display: "flex", flexDirection: "column", gap: 12,
                       overflowY: "auto", flex: 1 }}>
          {clientName && (
            <div style={{ fontFamily: "Manrope", fontSize: 12, color: "#6b7280" }}>
              Клиент: <b>{clientName}</b>
            </div>
          )}

          <label style={{ display: "block" }}>
            <div style={editorLabel}>Категория</div>
            <select data-testid="wa-share-category" value={categorySlug}
              onChange={e => { setCategorySlug(e.target.value); setSuggestions([]); }}
              style={editorInput}>
              <option value="">Любая</option>
              {categories.map(c => (
                <optgroup key={c.slug} label={c.name}>
                  <option value={c.slug}>Всё «{c.name}»</option>
                  {(c.subcategories || []).map(sub => (
                    <option key={sub.slug} value={sub.slug}>{sub.name}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </label>

          <label style={{ display: "block" }}>
            <div style={editorLabel}>Макс. цена (₸) — необязательно</div>
            <input data-testid="wa-share-price-max" value={priceMax}
              onChange={e => { setPriceMax(e.target.value.replace(/[^0-9]/g, ""));
                                 setSuggestions([]); }}
              placeholder="500000"
              style={editorInput} />
          </label>

          <button data-testid="wa-share-suggest" onClick={suggest} disabled={loading}
            style={{ background: "#f5f5f4", border: "1px solid #e5e7eb", borderRadius: 6,
                     padding: "9px 14px", cursor: loading ? "wait" : "pointer",
                     fontFamily: "Manrope", fontSize: 12, fontWeight: 600,
                     color: "#3f3f46", display: "flex", alignItems: "center",
                     justifyContent: "center", gap: 6 }}>
            {loading ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
            {suggestions.length ? "Обновить подборку" : "Подобрать 5 товаров"}
          </button>

          {error && (
            <div style={{ background: "#fef2f2", border: "1px solid #fecaca",
                           color: "#991b1b", fontFamily: "Manrope", fontSize: 12,
                           padding: "8px 10px", borderRadius: 6 }}>
              {error}
            </div>
          )}

          {suggestions.length > 0 && (
            <div style={{ display: "grid", gap: 8 }}>
              <div style={{ fontFamily: "Manrope", fontSize: 11, color: "#6b7280",
                             textTransform: "uppercase", letterSpacing: "0.08em" }}>
                Найдено {suggestions.length}
              </div>
              {suggestions.map(s => (
                <div key={s.id}
                  style={{ display: "flex", gap: 10, alignItems: "center",
                           padding: "6px 8px", background: "#fafaf9",
                           border: "1px solid #e5e7eb", borderRadius: 6 }}>
                  {s.image ? (
                    <img src={s.image} alt="" loading="lazy"
                      style={{ width: 42, height: 42, objectFit: "cover",
                                borderRadius: 4, flexShrink: 0 }} />
                  ) : (
                    <div style={{ width: 42, height: 42, background: "#f5f5f4",
                                    borderRadius: 4, flexShrink: 0 }} />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: "Manrope", fontSize: 12, fontWeight: 600,
                                    color: "#0A0A0A", overflow: "hidden",
                                    textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {s.name}
                    </div>
                    <div style={{ fontFamily: "Manrope", fontSize: 11, color: "#8a8a89" }}>
                      {(s.price || 0).toLocaleString("ru-RU")} ₸ · {s.brand || ""}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ padding: "12px 18px", borderTop: "1px solid #e5e7eb",
                       display: "flex", justifyContent: "flex-end", gap: 8,
                       background: "#fafaf9" }}>
          <button onClick={onCancel}
            style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 6,
                     padding: "9px 16px", cursor: "pointer", fontFamily: "Manrope",
                     fontSize: 12 }}>
            Отмена
          </button>
          <button data-testid="wa-share-insert" onClick={createAndInsert}
            disabled={!suggestions.length || loading}
            style={{ background: (!suggestions.length || loading) ? "#e5e7eb" :
                                    "linear-gradient(135deg, #25D366, #128C7E)",
                     border: "none", borderRadius: 6, padding: "9px 18px",
                     color: "white", cursor: (!suggestions.length || loading) ? "not-allowed" : "pointer",
                     fontFamily: "Manrope", fontSize: 12, fontWeight: 700,
                     display: "flex", alignItems: "center", gap: 6 }}>
            {loading ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
            Вставить ссылку в ответ
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Compact strip under the chat header that surfaces the boutique's
 * address / phone / hours from the Knowledge Base. Clicking any pill
 * copies the value to the clipboard AND inserts it into the textarea —
 * handy for answers like "адрес: …" without opening the KB tab.
 */
function ContactStrip({ contacts, onInsert }) {
  const [flashKey, setFlashKey] = useState("");
  const items = [
    contacts.address && { key: "address", icon: MapPin, label: contacts.address, insert: `📍 ${contacts.address}` },
    contacts.hours   && { key: "hours",   icon: Clock,  label: contacts.hours,   insert: `Часы работы: ${contacts.hours}` },
    contacts.phone   && { key: "phone",   icon: Phone,  label: contacts.phone,   insert: `Наш телефон: ${contacts.phone}` },
  ].filter(Boolean);
  if (!items.length) return null;

  const handle = (it) => {
    try { navigator.clipboard?.writeText(it.label); } catch { /* noop */ }
    onInsert(it.insert);
    setFlashKey(it.key);
    setTimeout(() => setFlashKey(""), 1200);
  };

  return (
    <div data-testid="wa-contact-strip"
      style={{
        display: "flex", flexWrap: "wrap", gap: 6, padding: "6px 10px",
        background: "linear-gradient(90deg, #FDF6E8, #FFFDF7)",
        borderBottom: "1px solid #F3E2B8",
      }}>
      {items.map(it => {
        const Icon = it.icon;
        const flashed = flashKey === it.key;
        return (
          <button key={it.key} onClick={() => handle(it)}
            data-testid={`wa-contact-${it.key}`}
            title={`Вставить в ответ + скопировать: ${it.label}`}
            style={{
              display: "flex", alignItems: "center", gap: 5,
              background: flashed ? "#DCF8C6" : "white",
              border: "1px solid " + (flashed ? "#25D366" : "#F3E2B8"),
              borderRadius: 999, padding: "3px 9px",
              fontFamily: "Manrope", fontSize: 11, color: "#7A5A15",
              cursor: "pointer", maxWidth: 320, minWidth: 0,
              transition: "background 0.2s, border-color 0.2s",
            }}>
            <Icon size={11} style={{ flexShrink: 0 }} />
            <span style={{ overflow: "hidden", textOverflow: "ellipsis",
                            whiteSpace: "nowrap" }}>
              {it.label}
            </span>
            {flashed ? <span style={{ fontSize: 10 }}>✓</span> : <Copy size={10} style={{ opacity: 0.5 }} />}
          </button>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------------- */
/*  Forward modal — pick a client, resend text or media in-flight        */
/* ------------------------------------------------------------------- */
function ForwardModal({ msg, onClose, isAtelier = false,
                         base = `${API}/admin/whatsapp` }) {
  const [query, setQuery] = useState("");
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pickedId, setPickedId] = useState("");
  const [caption, setCaption] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  // Debounced client search
  useEffect(() => {
    let alive = true;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const params = { limit: 30 };
        if (query.trim()) params.search = query.trim();
        const r = await axios.get(
          isAtelier ? `${API}/admin/atelier/orders` : `${API}/admin/crm/clients`,
          { params, withCredentials: true });
        if (alive) setClients((r.data.items || []).filter(c => c.phone));
      } catch { /* noop */ }
      finally { if (alive) setLoading(false); }
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [query]);

  const submit = async () => {
    if (!pickedId || sending) return;
    setSending(true); setError("");
    try {
      await axios.post(`${base}/forward/${msg.id}`,
        isAtelier ? { to_order_id: pickedId, caption }
                  : { to_client_id: pickedId, caption },
        { withCredentials: true });
      onClose(true);
    } catch (e) {
      setError(e.response?.data?.detail || "Не удалось переслать");
    } finally { setSending(false); }
  };

  const isMedia = !!msg.media_url;
  const iconFor = (t) => t === "image" ? <ImageIcon size={14} />
                      : t === "video" ? <Video size={14} />
                      : t === "audio" ? <Volume2 size={14} />
                      : <File size={14} />;

  return createPortal(
    <div data-testid="forward-modal"
      onClick={() => onClose(false)}
      style={{ position: "fixed", inset: 0, background: "rgba(10,10,10,0.55)",
                zIndex: 250, display: "flex", alignItems: "center",
                justifyContent: "center", padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: "white", borderRadius: 12, padding: 22,
                  maxWidth: 540, width: "100%", maxHeight: "90vh",
                  overflowY: "auto", fontFamily: "Manrope" }}>
        <div style={{ display: "flex", justifyContent: "space-between",
                       alignItems: "center", marginBottom: 12 }}>
          <div style={{ fontFamily: "Cormorant Garamond", fontSize: 22, color: "#0A0A0A" }}>
            Переслать сообщение
          </div>
          <button onClick={() => onClose(false)}
            style={{ background: "none", border: "none", cursor: "pointer" }}>
            <X size={18} />
          </button>
        </div>

        {/* Preview of the source message */}
        <div style={{ background: "#f8fafc", border: "1px solid #e5e7eb",
                       borderRadius: 8, padding: 10, marginBottom: 14,
                       maxHeight: 120, overflow: "hidden" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6,
                         fontSize: 11, color: "#64748b", marginBottom: 4 }}>
            {isMedia && iconFor(msg.media_type)}
            <span>{isMedia ? `${msg.media_type} · ${msg.media_filename || ""}` : "Текст"}</span>
          </div>
          <div style={{ fontSize: 13, color: "#0A0A0A", whiteSpace: "pre-wrap",
                         maxHeight: 80, overflow: "hidden", textOverflow: "ellipsis" }}>
            {(msg.text || "").slice(0, 220)}
            {(msg.text || "").length > 220 && "…"}
          </div>
        </div>

        {/* Search field */}
        <label style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.12em",
                         color: "#8a8a89", fontWeight: 700, marginBottom: 4, display: "block" }}>
          Кому переслать
        </label>
        <div style={{ position: "relative", marginBottom: 8 }}>
          <Search size={13} style={{ position: "absolute", left: 10, top: "50%",
                                       transform: "translateY(-50%)", color: "#aaa" }} />
          <input
            data-testid="forward-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Имя или телефон…"
            autoFocus
            style={{ width: "100%", padding: "10px 10px 10px 32px", borderRadius: 6,
                     border: "1px solid #e5e7eb", fontSize: 13, fontFamily: "Manrope",
                     boxSizing: "border-box" }} />
        </div>

        {/* Client list */}
        <div style={{ border: "1px solid #f1f5f9", borderRadius: 8,
                       maxHeight: 240, overflowY: "auto", marginBottom: 12 }}>
          {loading ? (
            <div style={{ padding: 20, textAlign: "center", color: "#94a3b8" }}>Ищем…</div>
          ) : clients.length === 0 ? (
            <div style={{ padding: 20, textAlign: "center", color: "#94a3b8", fontSize: 12 }}>
              Клиентов не найдено
            </div>
          ) : clients.map(c => (
            <div key={c.id}
              data-testid={`forward-client-${c.id}`}
              onClick={() => setPickedId(c.id)}
              style={{ padding: "10px 12px", cursor: "pointer",
                        borderBottom: "1px solid #f8fafc",
                        background: pickedId === c.id ? "rgba(197,160,89,0.10)" : "white",
                        borderLeft: pickedId === c.id ? "3px solid #C5A059" : "3px solid transparent",
                        display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#0A0A0A" }}>
                  {c.name || "—"}
                </div>
                <div style={{ fontSize: 11, color: "#6b7280" }}>{c.phone}</div>
              </div>
              {pickedId === c.id && <Forward size={14} style={{ color: "#C5A059" }} />}
            </div>
          ))}
        </div>

        {/* Optional caption / prefix note */}
        <label style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.12em",
                         color: "#8a8a89", fontWeight: 700, marginBottom: 4, display: "block" }}>
          Комментарий (необязательно)
        </label>
        <textarea
          data-testid="forward-caption"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          rows={2}
          placeholder={isMedia ? "Подпись к файлу" : "Прибавится перед пересылаемым текстом"}
          style={{ width: "100%", padding: "8px 10px", borderRadius: 6,
                   border: "1px solid #e5e7eb", fontSize: 13, fontFamily: "Manrope",
                   resize: "vertical", boxSizing: "border-box" }} />

        {error && (
          <div style={{ marginTop: 10, padding: 8, background: "#fef2f2",
                         color: "#7f1d1d", border: "1px solid #fecaca",
                         borderRadius: 6, fontSize: 12 }}>{error}</div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end",
                       gap: 8, marginTop: 14 }}>
          <button onClick={() => onClose(false)}
            style={{ padding: "9px 16px", background: "white", border: "1px solid #e5e7eb",
                     borderRadius: 6, cursor: "pointer", fontFamily: "Manrope", fontSize: 13 }}>
            Отмена
          </button>
          <button
            data-testid="forward-submit"
            onClick={submit}
            disabled={!pickedId || sending}
            style={{ padding: "9px 20px",
                     background: !pickedId || sending ? "#e5e7eb"
                              : "linear-gradient(135deg, #25D366, #128C7E)",
                     color: "white", border: "none", borderRadius: 6,
                     cursor: !pickedId || sending ? "not-allowed" : "pointer",
                     fontFamily: "Manrope", fontSize: 13, fontWeight: 700,
                     display: "flex", alignItems: "center", gap: 6 }}>
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Forward size={14} />}
            {sending ? "Отправляем…" : "Переслать"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

